import React from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Clock, AlertTriangle } from "lucide-react";
import { selfCareGuidanceService } from "@/services/selfCareGuidance";

interface SelfCareGuidanceProps {
  aiScore: number;
  category: string;
  selectedSymptoms: string[];
  additionalSymptoms: string[];
  answers: Record<string, any>;
  medicalEvidence?: any[];
}

const SelfCareGuidance = ({ aiScore, category, selectedSymptoms, additionalSymptoms, answers, medicalEvidence }: SelfCareGuidanceProps) => {
  const [recommendations, setRecommendations] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const loadRecommendations = async () => {
      try {
        const recs = await selfCareGuidanceService.generateSelfCareRecommendations(
          aiScore,
          category, 
          selectedSymptoms, 
          additionalSymptoms,
          answers,
          medicalEvidence
        );
        setRecommendations(recs);
      } catch (error) {
        console.error('Error loading self-care recommendations:', error);
        setRecommendations([]);
      } finally {
        setLoading(false);
      }
    };

    loadRecommendations();
  }, [aiScore, category, selectedSymptoms, additionalSymptoms, answers, medicalEvidence]);

  const escalationTriggers = selfCareGuidanceService.getEscalationTriggers(category);

  return (
    <div className="space-y-4">
      {recommendations.map((recommendation, index) => (
        <Card key={index} className="p-4 border-green-200 bg-green-50">
          <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <h3 className="text-lg font-semibold text-green-800">
                  {recommendation.title}
                </h3>
              </div>
              <Badge variant="outline" className="text-green-700 border-green-300">
                {recommendation.category}
              </Badge>
            </div>

            {/* Description */}
            <p className="text-sm text-green-700 leading-relaxed">
              {recommendation.description}
            </p>

            {/* Self-Care Actions */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-green-800 flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Recommended Self-Care Actions:
              </h4>
              
              <div className="grid gap-2">
                {recommendation.actions.map((action, actionIndex) => (
                  <div key={actionIndex} className="flex items-start space-x-3">
                    <div className="bg-green-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                      {actionIndex + 1}
                    </div>
                    <p className="text-sm text-green-700 flex-1">
                      {action}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Duration */}
            <div className="flex items-center space-x-2 p-3 bg-green-100 rounded-md">
              <Clock className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-green-800">Duration:</span>
              <span className="text-sm text-green-700">{recommendation.duration}</span>
            </div>

            {/* Warnings */}
            {recommendation.warnings.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-green-800 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Important Warnings:
                </h4>
                <div className="space-y-1">
                  {recommendation.warnings.map((warning, warningIndex) => (
                    <div key={warningIndex} className="flex items-start space-x-2">
                      <div className="text-green-600 text-xs mt-1">•</div>
                      <p className="text-xs text-green-700 opacity-90">
                        {warning}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>
      ))}

      {/* Escalation Triggers */}
      <Card className="p-4 border-yellow-200 bg-yellow-50">
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
            <h3 className="text-lg font-semibold text-yellow-800">
              When to Seek Professional Care
            </h3>
          </div>
          
          <p className="text-sm text-yellow-700">
            Contact your healthcare provider or seek urgent care if you experience any of the following:
          </p>

          <div className="space-y-2">
            {escalationTriggers.map((trigger, index) => (
              <div key={index} className="flex items-start space-x-2">
                <div className="text-yellow-600 text-sm mt-1">⚠</div>
                <p className="text-sm text-yellow-700">
                  {trigger}
                </p>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default SelfCareGuidance;